import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Radio,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { Images } from '../../common';
import { TextInput } from '../../components/muiCustomized/TextInput';
import AWSLogoSVG from '../../images/awsLogo.svg';
import MinIOLogoSVG from '../../images/minioLogo.svg';
import './settings.css';

function DataStorage() {
  const { activeWorkspace } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dataRetentionDays, setDataRetentionDays] = useState(null);
  const [storageSettings, setStorageSettings] = useState({
    storageOption: 'Shoot',
    awsBucketName: '',
    awsAccessKeyId: '',
    awsSecretAccessKey: '',
    minioEndpoint: '',
  });
  const { shootLogo } = Images;

  useEffect(() => {
    const fetchStorageSettings = async () => {
      try {
        setLoading(true);
        const response = await sendRequest({
          method: 'get',
          url: API.ORGANIZATION.getStorageSettings(activeWorkspace.organizationId),
        });
        if (response.success) {
          setStorageSettings({
            storageOption: response.data?.data?.storageOption || 'Shoot',
            awsBucketName: response.data?.data?.awsBucketName || '',
            awsAccessKeyId: response.data?.data?.awsAccessKeyId || '',
            awsSecretAccessKey: '',
            minioEndpoint: response.data?.data?.minioEndpoint || '',
          });
        }

        // Fetch data retention from active subscription instance
        const featuresResponse = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.features(activeWorkspace.id),
        });

        if (featuresResponse.success && featuresResponse.data?.features) {
          const retentionDays = featuresResponse.data.features['Data Retention'];
          if (retentionDays) {
            setDataRetentionDays(retentionDays);
          }
        }
      } catch (err) {
        setError('Failed to load storage settings');
        // eslint-disable-next-line no-console
        console.error('Error fetching storage settings:', err);
      } finally {
        setLoading(false);
      }
    };

    if (activeWorkspace?.organizationId && activeWorkspace?.id) {
      fetchStorageSettings();
    }
  }, [activeWorkspace?.organizationId, activeWorkspace?.id]);

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Validate AWS/MinIO fields if AWS or MinIO is selected
    if (storageSettings.storageOption === 'AWS') {
      if (!storageSettings.awsBucketName || !storageSettings.awsAccessKeyId) {
        setError('Please fill in all required AWS fields');
        return;
      }
    }

    if (storageSettings.storageOption === 'MinIO') {
      if (!storageSettings.awsBucketName || !storageSettings.awsAccessKeyId
        || !storageSettings.minioEndpoint) {
        setError('Please fill in all required MinIO fields');
        return;
      }
    }

    try {
      setSaving(true);
      const response = await sendRequest({
        method: 'put',
        url: API.ORGANIZATION.updateStorageSettings(activeWorkspace.organizationId),
        data: storageSettings,
      });

      if (response.success) {
        setSuccess('Storage settings updated successfully');
        // Clear the secret key field after successful save
        setStorageSettings((prev) => ({
          ...prev,
          awsSecretAccessKey: '',
          minioEndpoint: response.data.minioEndpoint || prev.minioEndpoint,
        }));
      } else {
        setError(response.message || 'Failed to update storage settings');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update storage settings');
      // eslint-disable-next-line no-console
      console.error('Error updating storage settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
      <Typography
        variant="h5"
        sx={{
          mb: 1,
          color: '#0E1E40',
          fontWeight: 600,
        }}
      >
        Data Storage Settings
      </Typography>
      <Typography
        sx={{
          mb: 3,
          color: '#546077',
        }}
      >
        Choose where your data will be stored. Screenshots and webcam data will be saved to your
        selected storage provider.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ mb: 2, color: '#0E1E40', fontWeight: 500 }}>
          Storage Provider
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ position: 'relative', flex: '1 1 45%', minWidth: '250px' }}>
            <Radio
              name="storageOption"
              checked={storageSettings.storageOption === 'Shoot'}
              onChange={(e) => setStorageSettings({ ...storageSettings, storageOption: e.target.value })}
              value="Shoot"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                border: '2px solid',
                borderColor: storageSettings.storageOption === 'Shoot' ? '#213D69' : '#E2E2E2',
                borderRadius: '8px',
                padding: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: storageSettings.storageOption === 'Shoot' ? 'rgba(33, 61, 105, 0.05)' : 'white',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#213D69',
                  backgroundColor: 'rgba(33, 61, 105, 0.05)',
                },
              }}
              onClick={() => setStorageSettings({ ...storageSettings, storageOption: 'Shoot' })}
            >
              <img
                src={shootLogo}
                width="64"
                height="64"
                alt="shoot-logo"
                style={{ marginBottom: '16px' }}
              />
              <Typography sx={{ fontSize: '14px', color: '#546077' }}>
                Shoot can store your data on your behalf.
              </Typography>
              {dataRetentionDays && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#546077',
                    marginTop: '8px',
                    fontWeight: 500,
                  }}
                >
                  Data Retention:
                  {' '}
                  {dataRetentionDays}
                  {' '}
                  day
                  {dataRetentionDays !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ position: 'relative', flex: '1 1 45%', minWidth: '250px' }}>
            <Radio
              name="storageOption"
              checked={storageSettings.storageOption === 'AWS'}
              onChange={(e) => setStorageSettings({ ...storageSettings, storageOption: e.target.value })}
              value="AWS"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                border: '2px solid',
                borderColor: storageSettings.storageOption === 'AWS' ? '#213D69' : '#E2E2E2',
                borderRadius: '8px',
                padding: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: storageSettings.storageOption === 'AWS' ? 'rgba(33, 61, 105, 0.05)' : 'white',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#213D69',
                  backgroundColor: 'rgba(33, 61, 105, 0.05)',
                },
                minHeight: '188px',
              }}
              onClick={() => setStorageSettings({ ...storageSettings, storageOption: 'AWS' })}
            >
              <img
                src={AWSLogoSVG}
                width="64"
                alt="aws-logo"
                style={{ marginBottom: '16px', marginTop: '16px' }}
              />
              <Typography sx={{ fontSize: '14px', color: '#546077' }}>
                Use AWS to store your own data.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', flex: '1 1 45%', minWidth: '250px' }}>
            <Radio
              name="storageOption"
              checked={storageSettings.storageOption === 'MinIO'}
              onChange={(e) => setStorageSettings({ ...storageSettings, storageOption: e.target.value })}
              value="MinIO"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                border: '2px solid',
                borderColor: storageSettings.storageOption === 'MinIO' ? '#213D69' : '#E2E2E2',
                borderRadius: '8px',
                padding: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: storageSettings.storageOption === 'MinIO' ? 'rgba(33, 61, 105, 0.05)' : 'white',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#213D69',
                  backgroundColor: 'rgba(33, 61, 105, 0.05)',
                },
              }}
              onClick={() => setStorageSettings({ ...storageSettings, storageOption: 'MinIO' })}
            >
              <img
                src={MinIOLogoSVG}
                width="64"
                alt="minio-logo"
                style={{ marginBottom: '16px', marginTop: '16px' }}
              />
              <Typography sx={{ fontSize: '14px', color: '#546077' }}>
                Use MinIO for On-Premise storage.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {storageSettings.storageOption === 'Shoot' && dataRetentionDays && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ backgroundColor: '#E3F2FD', color: '#0D47A1' }}>
            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
              Shoot Managed Storage
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              Your data is securely stored by Shoot with a retention period of
              {' '}
              <strong>
                {dataRetentionDays}
                {' '}
                day
                {dataRetentionDays !== 1 ? 's' : ''}
              </strong>
              .
              Screenshots and webcam data will be automatically deleted
              after this period.
            </Typography>
          </Alert>
        </Box>
      )}

      {storageSettings.storageOption === 'AWS' && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 2, color: '#0E1E40', fontWeight: 500 }}>
            AWS Configuration
          </Typography>

          <Link
            to="/organization-signup/aws-setup"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#213D69',
              textDecoration: 'none',
              display: 'inline-block',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            Follow our guide to setup your AWS data storage →
          </Link>

          <TextInput
            type="text"
            name="awsBucketName"
            autoComplete="off"
            value={storageSettings.awsBucketName}
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="AWS S3 Bucket Name"
            placeholder="Enter your S3 bucket name"
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextInput
            name="awsAccessKeyId"
            value={storageSettings.awsAccessKeyId}
            autoComplete="off"
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="AWS Access Key ID"
            placeholder="Enter your AWS Access Key ID"
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextInput
            type="password"
            name="awsSecretAccessKey"
            autoComplete="new-password"
            value={storageSettings.awsSecretAccessKey}
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="AWS Secret Access Key"
            placeholder="Enter your AWS Secret Access Key (leave empty to keep current)"
            fullWidth
            helperText="Leave empty to keep your current secret key"
          />
        </Box>
      )}

      {storageSettings.storageOption === 'MinIO' && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 2, color: '#0E1E40', fontWeight: 500 }}>
            MinIO Configuration
          </Typography>

          <TextInput
            type="text"
            name="minioEndpoint"
            autoComplete="off"
            value={storageSettings.minioEndpoint}
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="MinIO Endpoint URL"
            placeholder="https://minio.example.com"
            fullWidth
            sx={{ mb: 2 }}
            helperText="Enter the MinIO server endpoint URL"
          />

          <TextInput
            type="text"
            name="awsBucketName"
            autoComplete="off"
            value={storageSettings.awsBucketName}
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="Bucket Name"
            placeholder="Enter your bucket name"
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextInput
            name="awsAccessKeyId"
            value={storageSettings.awsAccessKeyId}
            autoComplete="off"
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="Access Key ID"
            placeholder="Enter your Access Key ID"
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextInput
            type="password"
            name="awsSecretAccessKey"
            autoComplete="new-password"
            value={storageSettings.awsSecretAccessKey}
            onChange={(e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value })}
            label="Secret Access Key"
            placeholder="Enter your Secret Access Key (leave empty to keep current)"
            fullWidth
            helperText="Leave empty to keep your current secret key"
          />
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: '#53A551',
            color: '#FFFFFF',
            textTransform: 'none',
            padding: '8px 32px',
            '&:hover': {
              backgroundColor: '#448843',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}

export default DataStorage;
